import type { ReactNode } from "react";
import { Box, Card, Skeleton, Stack, Typography } from "@mui/material";
import { formatNumber } from "../lib/format";

/** Consistent page title + optional subtitle/actions across the panel. */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
      mb={3}
    >
      <Box>
        <Typography variant="h5">{title}</Typography>
        {subtitle && (
          <Typography color="text.secondary" variant="body2" mt={0.5}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
  );
}

type Tone = "primary" | "success" | "warning" | "info" | "secondary";

const TONE_BG: Record<Tone, string> = {
  primary: "rgba(14,107,92,0.10)",
  success: "rgba(27,122,67,0.12)",
  warning: "rgba(154,103,0,0.12)",
  info: "rgba(37,99,143,0.12)",
  secondary: "rgba(184,134,11,0.14)",
};
const TONE_FG: Record<Tone, string> = {
  primary: "#0E6B5C",
  success: "#1B7A43",
  warning: "#9A6700",
  info: "#25638F",
  secondary: "#8A6300",
};

/** KPI tile: icon chip + big value + label, hairline card. */
export function StatCard({
  icon,
  label,
  value,
  tone = "primary",
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: number | string | undefined;
  tone?: Tone;
  hint?: string;
}) {
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: TONE_BG[tone],
            color: TONE_FG[tone],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Typography color="text.secondary" variant="body2" sx={{ flex: 1 }}>
          {label}
        </Typography>
      </Stack>
      {value === undefined ? (
        <Skeleton width={72} height={44} />
      ) : (
        <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: "-0.5px", lineHeight: 1.1 }}>
          {typeof value === "number" ? formatNumber(value) : value}
        </Typography>
      )}
      {hint && (
        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
          {hint}
        </Typography>
      )}
    </Card>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" display="block" mt={4} mb={1.5}>
      {children}
    </Typography>
  );
}
